import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  IAddMemberToTeam,
  IBoostStatus,
  IRemoveMemberFromTeam,
  IRoleData,
  ITeam,
  ITeamAdd,
  ITeamEdit,
  IUpdateMemberToTeam,
  MemberRoleEnum,
  MemberTeamManagementRoleEnum,
} from "utils/api.interfaces";
import axiosinterceptor from "../utils/api.interceptor";

/**
 * Fetches team data for a given team ID
 * @param teamId - The ID of the team to fetch
 * @returns Promise containing the team data
 */
export const fetchTeam = (teamId: string | undefined): Promise<ITeam> =>
  axiosinterceptor.get(`/v1/Teams/${teamId}`).then<ITeam>((res) => res.data);

/**
 * Custom hook for fetching and managing team data
 * @param teamId - The ID of the team to fetch
 * @returns Object containing team data and loading state
 */
export default function useTeam(
  teamId: string | undefined,
  options?: Omit<
    UseQueryOptions<ITeam, Error, ITeam, ["team", string | undefined]>,
    "queryKey" | "queryFn"
  >
) {
  const queryTeam = useQuery({
    queryKey: ["team", teamId],
    queryFn: () => fetchTeam(teamId),
    enabled: !!teamId,
    ...options,
  });

  return {
    ...queryTeam,
    dataTeam: queryTeam?.data,
    dataTeamIsLoading: queryTeam?.isLoading,
  };
}

// Team Management Functions
/**
 * Creates a new team
 * @param team - Team data to create
 * @returns Promise containing the created team data
 */
export const postTeam = (team: ITeamAdd): Promise<ITeam> =>
  axiosinterceptor.post(`/v1/Teams`, team).then((res) => res.data);

/**
 * Updates an existing team
 * @param team - Team data to update
 * @returns Promise containing the updated team data
 */
export const putTeam = (team: ITeamEdit): Promise<IBoostStatus> =>
  axiosinterceptor.put(`/v1/Teams`, team).then((res) => res.data);

/**
 * Deletes a team
 * @param id - ID of the team to delete
 * @returns Promise containing the deletion result
 */
export const deleteTeam = (id: string): Promise<void> =>
  axiosinterceptor.delete(`/v1/Teams/${id}`).then((res) => res.data);

/**
 * Updates a team's official status (MM override path).
 * The API enforces permissions (403 for non club-admin/MM) and rejects
 * setting `true` for Default-Organisation teams.
 * @param teamId - ID of the team
 * @param isOfficial - New official status
 * @returns Promise resolving when the update succeeds
 */
export const putTeamOfficialStatus = (
  teamId: string,
  isOfficial: boolean
): Promise<void> =>
  axiosinterceptor
    .put(`/v1/Teams/official-status`, { teamId, isOfficial })
    .then((res) => res.data);

// Team Member Management Functions
/**
 * Removes a member from a team
 * @param teamId - ID of the team
 * @param memberId - ID of the member to remove
 * @returns Promise containing the removal result
 */
export const removeMemberFromTeam = async (
  teamId: string,
  memberId: string
): Promise<IRemoveMemberFromTeam> => {
  const res = await axiosinterceptor.delete(`/v1/Teams/member`, {
    data: { teamId, memberId },
  });
  return res.data;
};

/**
 * Adds a new member to a team
 * @param teamId - ID of the team
 * @param userId - ID of the user to add
 * @param roles - Array of roles for the member
 * @param isAdmin - Whether the member should be an admin
 * @returns Promise containing the addition result
 */
export const addMemberToTeam = async (
  teamId: string,
  userId: string,
  roles: MemberRoleEnum[],
  isAdmin: boolean
): Promise<IAddMemberToTeam> => {
  console.log("addMemberToTeam", teamId, userId, roles, isAdmin);
  return axiosinterceptor
    .post(`/v1/Teams/member`, { teamId, userId, roles, isAdmin })
    .then((res) => res.data);
};

/**
 * Updates a team member's roles and permissions
 * @param teamId - ID of the team
 * @param userId - ID of the member to update
 * @param teamManagementRoles - Array of management roles
 * @param rolesData - Array of member roles
 * @returns Promise containing the update result
 */
export const updateMemberToTeam = async (
  teamId: string,
  id: string,
  teamManagementRoles: MemberTeamManagementRoleEnum[],
  rolesData: IRoleData[]
): Promise<IUpdateMemberToTeam> =>
  axiosinterceptor
    .put(`/v1/Teams/member`, { teamId, id, teamManagementRoles, rolesData })
    .then((res) => res.data);

// Team Boost Management Functions
/**
 * Gets the boost status for a team
 * @param id - ID of the team
 * @returns Promise containing the boost status
 */
export const getTeamBoost = (id: string): Promise<IBoostStatus> =>
  axiosinterceptor
    .get(`/v1/TeamBoost/team/${id}/booststatus`)
    .then((res) => res.data);

/**
 * Gets the boost status for a specific user in a team
 * @param userId - ID of the user
 * @param teamId - ID of the team
 * @returns Promise containing the boost status
 */
export const getTeamBoostPerUser = (
  userId: string,
  teamId: string
): Promise<IBoostStatus> =>
  axiosinterceptor
    .get(`v1/TeamBoost/user/${userId}?teamFilter=${teamId}`)
    .then((res) => res.data);

/**
 * Gifts a team boost
 * @param id - ID of the team
 * @param expiresAtUtc - Expiration date in UTC
 * @returns Promise containing the boost result
 */
export const postTeamBoostGift = (
  id: string,
  expiresAtUtc: string
): Promise<IBoostStatus> =>
  axiosinterceptor
    .post(`/v1/TeamBoost/gift`, { teamId: id, expiresAtUtc })
    .then((res) => res.data);

/**
 * Unassigns a team boost
 * @param teamBoostId - ID of the team boost to unassign
 * @returns Promise containing the unassign result
 */
export const putTeamBoostUnassign = (
  teamBoostId: string
): Promise<IBoostStatus> =>
  axiosinterceptor
    .put(`/v1/TeamBoost/unassign`, { teamBoostId })
    .then((res) => res.data);

// Team Image Management
/**
 * Uploads a team logo image
 * @param image - File array containing the image
 * @returns Promise containing the upload result
 */
export const postTeamImage = (image: File[]): Promise<{ fileUrl: string }> => {
  const formData = new FormData();
  formData.append("imageFile", image[0]);

  return axiosinterceptor
    .post(`/v1/Teams/logo`, formData)
    .then((res) => res.data);
};

// Team Archive Management
/**
 * Archives a team
 * @param id - ID of the team to archive
 * @returns Promise containing the archive result
 */
export const putArchiveTeam = (id: string): Promise<void> =>
  axiosinterceptor.put(`/v1/Teams/archive`, { id }).then((res) => res.data);

/**
 * Unarchives a team
 * @param id - ID of the team to unarchive
 * @returns Promise containing the unarchive result
 */
export const putUnArchiveTeam = (id: string): Promise<void> =>
  axiosinterceptor.put(`/v1/Teams/unarchive`, { id }).then((res) => res.data);

/**
 * Archives multiple teams for a season
 * @param seasonId - ID of the season
 * @returns Promise containing the bulk archive result
 */
export const putBulkArchiveTeam = (seasonId: string): Promise<void> =>
  axiosinterceptor
    .put(`/v1/Teams/bulkarchive`, { seasonId })
    .then((res) => res.data);

export const restartTeamStats = (
  teamId: string
): Promise<{ success: boolean }> =>
  axiosinterceptor
    .post(`/v1/Stats/Team/${teamId}/restart-stats`)
    .then((res) => res.data);

export interface IRestartActiveTeamsResult {
  totalTeams: number;
  successCount: number;
  failedCount: number;
  failedTeamIds: string[];
}

export const restartActiveTeamsStats = (): Promise<IRestartActiveTeamsResult> =>
  axiosinterceptor
    .post(`/v1/Stats/restart-active-teams`)
    .then((res) => res.data);
